import { ThemeProvider, type Theme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ChevronDown } from "lucide-react";

const SelectIcon = ({ className }: { className?: string }) => <ChevronDown size={16} className={className} />;

const FIELDS: Theme["components"] = {
  MuiInputLabel: {
    defaultProps: { shrink: true },
    styleOverrides: {
      root: {
        position: "relative", transform: "none", maxWidth: "100%", pointerEvents: "auto", marginBottom: 6,
        fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: "var(--color-gray-700)",
        "&.Mui-focused, &.Mui-error": { color: "var(--color-gray-700)" },
        ".dark &, .dark &.Mui-focused, .dark &.Mui-error": { color: "var(--color-gray-400)" },
      },
    },
  },
  MuiOutlinedInput: {
    defaultProps: { notched: false },
    styleOverrides: {
      root: {
        borderRadius: 8, fontSize: 14, backgroundColor: "transparent",
        "&.MuiInputBase-multiline": { padding: "10px 12px" },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-gray-300)" },
        ".dark &": { color: "rgba(255, 255, 255, 0.9)" },
        ".dark & .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-gray-700)" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-gray-400)" },
        "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(70, 95, 255, 0.15)" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-brand-500)", borderWidth: 1 },
        "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-error-500)" },
        "&.Mui-focused.Mui-error": { boxShadow: "0 0 0 3px rgba(240, 68, 56, 0.15)" },
        "&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-error-500)" },
        "& .MuiSelect-icon, & .MuiNativeSelect-icon": { right: 12, top: "calc(50% - 8px)", color: "var(--color-gray-500)" },
      },
      input: {
        height: 40, boxSizing: "border-box", padding: "0 12px",
        "td &": { height: 36 },
        "&.MuiInputBase-inputMultiline": { height: "auto", padding: 0 },
        "&.MuiSelect-select.MuiInputBase-input": { height: 40, minHeight: 0, display: "flex", alignItems: "center", "td &": { height: 36 } },
        "&::placeholder": { color: "var(--color-gray-400)", opacity: 1 },
      },
    },
  },
  MuiSelect: { defaultProps: { IconComponent: SelectIcon, notched: false } },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        height: 32, padding: "0 12px", borderRadius: 8, fontSize: 13, fontWeight: 500, lineHeight: 1, textTransform: "none", whiteSpace: "nowrap",
        color: "var(--color-gray-700)", borderColor: "var(--color-gray-300)",
        "&.Mui-selected": { backgroundColor: "var(--color-gray-800)", color: "#fff", "&:hover": { backgroundColor: "var(--color-gray-900)" } },
        ".dark &": { color: "var(--color-gray-300)", borderColor: "var(--color-gray-700)" },
        ".dark &.Mui-selected": { backgroundColor: "rgba(255, 255, 255, 0.9)", color: "var(--color-gray-900)", "&:hover": { backgroundColor: "#fff" } },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: { root: { margin: "6px 0 0", fontSize: 12, "&.Mui-error": { color: "var(--color-error-600)" } } },
  },
  MuiAutocomplete: {
    styleOverrides: { root: { "& .MuiOutlinedInput-root, & .MuiOutlinedInput-root.MuiInputBase-sizeSmall": { paddingTop: 0, paddingBottom: 0 } } },
  },
};

// Defined once so the nested theme is built once per outer theme, not on every render.
const withFields = (outer: Theme): Theme => ({ ...outer, components: { ...outer.components, ...FIELDS } });

export default function FieldTheme({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider theme={withFields}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>{children}</LocalizationProvider>
    </ThemeProvider>
  );
}
